/**
 * Built-in payment method identifiers used by checkout, receipts, and confirm-payment-mode APIs.
 * API-driven custom methods may still arrive as free-form strings.
 *
 * Named `PaymentMethodCode` (not `PaymentMethod`) to avoid colliding with existing
 * `PaymentMethod` string-union types in checkout state services.
 *
 * UI vs system-only:
 * - **Checkout UI** — staff pick Cash, Card, Link, TapToPay, or Terminal in payment forms.
 * - **System / persisted** — CardPresent is written to the order after a successful in-person
 *   collection (tap-to-pay or terminal reader), not selected directly in the UI.
 */
export enum PaymentMethodCode {
  /** Checkout UI — confirm cash received; opens cash drawer when Cash is selected. */
  Cash = 'cash',

  /** Checkout UI — staff enter card details in-browser via Stripe Elements (card-not-present). */
  Card = 'card',

  /**
   * System only — persisted on the order after successful in-person card collection
   * (tap-to-pay or Stripe Terminal). Sent via `confirm-payment-mode`; not a checkout tile.
   */
  CardPresent = 'card_present',

  /** Checkout UI — send a payment link by SMS. */
  Link = 'link',

  /** Checkout UI — mobile/Android flow; opens native tap-to-pay app (NFC). Records CardPresent on success. */
  TapToPay = 'tap-to-pay',

  /** Checkout UI — in-app Stripe Terminal reader collection. Records CardPresent on success. */
  Terminal = 'terminal',
}

/** Site-specific / API-driven payment modes confirmed via confirm-payment-mode. */
export type DynamicPaymentMethodCode =
  | 'DRB Membership'
  | 'Prepaid'
  | 'Everwash Membership'
  | 'Washify Membership'
  | 'iWashPOS Membership'
  | 'Way.com Membership';

/** Built-in checkout methods (excludes API-driven custom methods). */
export type BuiltInPaymentMethodCode =
  | PaymentMethodCode.Cash
  | PaymentMethodCode.Card
  | PaymentMethodCode.Link
  | PaymentMethodCode.TapToPay
  | PaymentMethodCode.Terminal;

/** Any method selectable in checkout UI or sent to confirm-payment-mode. */
export type CheckoutPaymentMethodCode = BuiltInPaymentMethodCode | DynamicPaymentMethodCode;

export const BUILT_IN_PAYMENT_METHOD_CODES: readonly BuiltInPaymentMethodCode[] = [
  PaymentMethodCode.TapToPay,
  PaymentMethodCode.Terminal,
  PaymentMethodCode.Card,
  PaymentMethodCode.Link,
  PaymentMethodCode.Cash,
];

const PAYMENT_METHOD_ALIASES: Record<string, BuiltInPaymentMethodCode> = {
  [PaymentMethodCode.Cash]: PaymentMethodCode.Cash,
  [PaymentMethodCode.Card]: PaymentMethodCode.Card,
  [PaymentMethodCode.Link]: PaymentMethodCode.Link,
  [PaymentMethodCode.Terminal]: PaymentMethodCode.Terminal,
  [PaymentMethodCode.TapToPay]: PaymentMethodCode.TapToPay,
  taptopay: PaymentMethodCode.TapToPay,
};

export function normalizePaymentMethodValue(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

export function isPaymentMethodCode(
  value: string | null | undefined,
  code: PaymentMethodCode
): boolean {
  return normalizePaymentMethodValue(value) === code;
}

export function isBuiltInPaymentMethodCode(
  value: string | null | undefined
): value is BuiltInPaymentMethodCode {
  const normalized = normalizePaymentMethodValue(value);
  return BUILT_IN_PAYMENT_METHOD_CODES.includes(normalized as BuiltInPaymentMethodCode);
}

export function resolveBuiltInPaymentMethodCode(
  value: string | null | undefined
): BuiltInPaymentMethodCode | null {
  const normalized = normalizePaymentMethodValue(value);
  const resolved = PAYMENT_METHOD_ALIASES[normalized];
  return resolved ?? null;
}
